import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../utils/tokenManager";
import axios from "axios";
import Button from "../common/Button";
import logoIcon from "../../assets/common/logo-icon.svg";
import logout from "../../assets/common/logout.svg";
import warning from "../../assets/agent/warning.svg";
import success from "../../assets/agent/success.svg";
import fail from "../../assets/agent/fail.svg";
import AdminSidebar from "components/common/AdminSidebar";

function ClientConfirm() {
  const { clientId } = useParams();
  const [formData, setFormData] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = localStorage.getItem("adminSidebarPinned");
    return saved === "true";
  });

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const accessToken = await getValidToken();
        const res = await axios.get(
          `${process.env.REACT_APP_APIBASEURL}/api/clients/${clientId}`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        const client = res.data;
        if (client.EmailNotifications && client.EmailNotifications.cc) {
          client.Secondary_Emails = client.EmailNotifications.cc.join(", ");
        }
        setFormData(client);
      } catch (err) {
        console.error(err);
      }
    };
    fetchClient();
  }, [clientId]);

  const handleUpdateClient = async () => {
    try {
      const accessToken = await getValidToken();
      const newFormData = {
        Address: formData.Address || "",
        Contact_Email: formData.Contact_Email || "",
        Contact_Person: formData.Contact_Person || "",
        Contact_Phone: formData.Contact_Phone || "",
        Dialog: formData.Dialog || "",
        Name: formData.Name || "",
        PreferredLanguage: formData.PreferredLanguage || "es",
        Product: formData.Product || "",
        Url_Calendar: formData.Url_Calendar || "",
        EmailNotifications: {
          bcc: [],
          cc: formData.Secondary_Emails
            ? formData.Secondary_Emails.split(",")
                .map((email) => email.trim())
                .filter((email) => email !== "")
            : [],
        },
      };
      setShowConfirm(false);
      await axios.put(
        `${process.env.REACT_APP_APIBASEURL}/api/clients/${clientId}`,
        newFormData,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      setSuccessModal(true);
    } catch (error) {
      const errorMessage = error.response?.data?.error || t("error.generic");
      setErrorModal(true);
      setErrors({ submit: errorMessage });
    }
  };

  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (successModal) {
      const timer = setTimeout(() => {
        setSuccessModal(false);
        navigate("/dashboard");
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [successModal, navigate]);

  return (
    <div className="flex min-h-screen bg-secondaryBg">
      <AdminSidebar
        logoIcon={logoIcon}
        logout={logout}
        labelClassName="bg-primary"
        onToggle={(pinned) => setSidebarPinned(pinned)}
      />
      <div
        className={`flex-1 flex flex-col px-4 sm:px-8 py-6 md:py-10 transition-all duration-300 ${
          sidebarPinned ? "ml-[250px]" : "ml-[80px]"
        }`}
      >
        <div className="max-w-6xl mx-auto w-full">
          <h1 className="text-[50px] font-bold text-primary mb-2">
            {t("dashboard.editClient.head")}
          </h1>
          <p className="mb-6 md:text-xl text-primary">
            {t("dashboard.editClient.desc")}
          </p>
          <div className="relative flex items-center mb-8">
            <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
            <hr className="flex-1 border-t-2 border-[#0A1E4C]" />
            <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setShowConfirm(true);
            }}
          >
            <div className="overflow-hidden border border-stroke rounded-lg bg-white mb-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 border-b">
                <div className="flex">
                  <div className="p-5 bg-lightBlue w-[165px] text-primary">
                    {t("client.company")}
                  </div>
                  <input
                    type="text"
                    className="p-5 w-full h-auto"
                    value={formData.Name || ""}
                    disabled
                  />
                </div>
                <div className="flex">
                  <div className="p-5 bg-lightBlue w-[165px] text-primary">
                    {t("client.product")}
                  </div>
                  <input
                    type="text"
                    value={formData.Product || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, Product: e.target.value })
                    }
                    className="p-5 w-full h-auto"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 border-b">
                <div className="flex">
                  <div className="p-5 bg-lightBlue w-[165px] text-primary">
                    {t("client.address")}
                  </div>
                  <input
                    type="text"
                    value={formData.Address || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, Address: e.target.value })
                    }
                    className="p-5 w-full h-auto"
                  />
                </div>
                <div className="flex">
                  <div className="p-5 bg-lightBlue w-[165px] text-primary">
                    {t("client.urlCalendar")}
                  </div>
                  <input
                    type="text"
                    value={formData.Url_Calendar || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, Url_Calendar: e.target.value })
                    }
                    className="p-5 w-full h-auto"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 border-b">
                <div className="flex">
                  <div className="p-5 bg-lightBlue w-[165px] text-primary">
                    {t("client.contactPhone")}
                  </div>
                  <input
                    type="text"
                    value={formData.Contact_Phone || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        Contact_Phone: e.target.value,
                      })
                    }
                    className="p-5 w-full h-auto"
                  />
                </div>
                <div className="flex">
                  <div className="p-5 bg-lightBlue w-[165px] text-primary">
                    {t("client.contactPerson")}
                  </div>
                  <input
                    type="text"
                    value={formData.Contact_Person || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        Contact_Person: e.target.value,
                      })
                    }
                    className="p-5 w-full h-auto"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 border-b">
                <div className="flex">
                  <div className="p-5 bg-lightBlue w-[165px] text-primary">
                    {t("client.contactEmail")}
                  </div>
                  <input
                    type="text"
                    value={formData.Contact_Email || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        Contact_Email: e.target.value,
                      })
                    }
                    className="p-5 w-full h-auto"
                  />
                </div>
                <div className="flex">
                  <div className="p-5 bg-lightBlue w-[165px] text-primary">
                    {t("client.script")}
                  </div>
                  <input
                    type="text"
                    value={formData.Dialog || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, Dialog: e.target.value })
                    }
                    className="p-5 w-full h-auto"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 border-b">
                <div className="flex">
                  <div className="p-5 bg-lightBlue w-[165px] text-primary">
                    {t("client.preferredLangauge")}
                  </div>
                  <select
                    value={formData.PreferredLanguage || "es"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        PreferredLanguage: e.target.value,
                      })
                    }
                    className="p-5 w-full h-auto bg-white"
                  >
                    <option value="es">Spanish</option>
                    <option value="en">English</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-1 border-b">
                <div className="flex">
                  <div className="p-5 bg-lightBlue w-[165px] text-primary">
                    {t("client.secondaryEmail")}
                  </div>
                  <input
                    type="text"
                    value={formData.Secondary_Emails || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        Secondary_Emails: e.target.value,
                      })
                    }
                    className="p-5 w-full h-auto"
                    placeholder="email1@example.com, email2@example.com"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="primary" className="w-40">
                {t("admin.userConfirm.btn")}
              </Button>
            </div>
          </form>

          <div className="mt-4">
            <button
              type="button"
              className="text-primary flex text-xl sm:text-2xl font-semibold"
              onClick={handleBack}
            >
              {t("back")}
            </button>
          </div>
        </div>
      </div>
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-2xl text-primary text-xl shadow-xl w-full max-w-[660px] p-7 relative flex flex-col items-center space-y-6">
            <img src={warning} alt="warning" className="w-[60px] h-[60px]" />
            <p className="mb-1 text-xl font-bold text-center">
              {t("dashboard.editClient.saveChange")}
            </p>

            <div className="flex gap-14 flex-wrap justify-center">
              <Button
                variant="secondary"
                className="w-full sm:w-48 px-4 py-2"
                onClick={() => setShowConfirm(false)}
              >
                {t("agent.nextCall.keep")}
              </Button>
              <Button
                variant="primary"
                className="w-full sm:w-48 px-4 py-2"
                onClick={handleUpdateClient}
              >
                {t("agent.noteInfo.yes")}, {t("continue")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {successModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img
              src={success}
              alt="success"
              className="w-[60px] h-[60px] mb-4"
            />
            <p className="mb-4 text-xl font-bold">
              {t("admin.clientConfirm.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            <p className="mb-6 text-xl">{t("admin.clientConfirm.desc")}</p>
          </div>
        </div>
      )}

      {errorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img src={fail} alt="fail" className="w-[60px] h-[60px] mb-4" />
            <p className="mb-4 text-xl font-bold">
              {t("admin.userError.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            {errors.submit && (
              <p className="mb-6 text-xl w-[455px] text-center">
                {errors.submit}
              </p>
            )}
            <Button variant="secondary" onClick={() => setErrorModal(false)}>
              {t("admin.userError.btn")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientConfirm;
